import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  Paper,
  Pagination,
  Stack,
  Box,
  Skeleton,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { Icon } from '@iconify/react';

const TableSiberat = ({
  columns = [],
  rows = [],
  dense = false,
  pagination = false,
  page = 1,
  pageCount = 1,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  footer = null,
  loading = false,
  total,
  emptyMessage = 'Data belum tersedia',
  hidePageSize = false
}) => {
  const skeletonRows = pageSize || 5;
  const cellPadding = dense ? '8px 10px' : '12px 12px';
  const computedTotal =
    total ??
    (page === pageCount
      ? (pageCount - 1) * pageSize + rows.length
      : pageCount * pageSize);

  const tableMinWidth = columns.reduce((sum, col) => {
    if (typeof col.minWidth === 'number') return sum + col.minWidth;
    if (typeof col.width === 'number') return sum + col.width;
    // percent / unset: keep readable column floor for mobile scroll
    return sum + 128;
  }, 0);

  const resolvedMinWidth = Math.max(tableMinWidth, 720);

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: pagination ? '8px 8px 0 0' : '8px',
          boxShadow: 'none',
          border: '1px solid #E5E7EB',
          borderBottom: pagination ? 'none' : undefined,
          opacity: loading ? 0.6 : 1,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            height: '6px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#D1D5DB',
            borderRadius: '4px'
          }
        }}>
        <Table
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            minWidth: resolvedMinWidth
          }}>
          <colgroup>
            {columns.map((c, i) => (
              <col
                key={i}
                style={{
                  width:
                    typeof c.width === 'number' ? `${c.width}px` : c.width
                }}
              />
            ))}
          </colgroup>

          <TableHead>
            <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
              {columns.map((col, i) => (
                <TableCell
                  key={i}
                  align={col.align || 'left'}
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#374151',
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em',
                    borderBottom: '1px solid #E5E7EB',
                    padding: cellPadding,
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array.from({ length: skeletonRows }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_, j) => (
                    <TableCell key={j} sx={{ padding: cellPadding }}>
                      <Skeleton height={20} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align='center'
                  sx={{ py: 8 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1.5,
                      color: 'text.secondary'
                    }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(244,180,0,0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                      <Icon
                        icon='solar:inbox-bold-duotone'
                        width={26}
                        style={{ color: '#F4B400' }}
                      />
                    </Box>
                    <Typography fontSize={14} fontWeight={600}>
                      {emptyMessage}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, i) => (
                <TableRow
                  key={row.id ?? i}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#F9FAFB'
                    }
                  }}>
                  {columns.map((col, j) => (
                    <TableCell
                      key={j}
                      align={col.align || 'left'}
                      sx={{
                        padding: cellPadding,
                        borderBottom: '1px solid #F1F5F9',
                        verticalAlign: col.verticalAlign || 'middle',
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                        overflow: col.overflow || 'hidden',
                        whiteSpace: col.nowrap ? 'nowrap' : undefined
                      }}>
                      {col.render
                        ? col.render(row[col.field], row, i)
                        : row[col.field] ?? '-'}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>

          {footer && (
            <TableFooter>
              <TableRow
                sx={{
                  backgroundColor: '#FFFBEB',
                  borderTop: '1px solid #E5E7EB'
                }}>
                {(() => {
                  const cells = [];
                  let skipUntil = 0;

                  columns.forEach((col, i) => {
                    if (i < skipUntil) return;

                    const cell = Array.isArray(footer)
                      ? footer[i]
                      : footer?.[i];

                    if (!cell) {
                      cells.push(<TableCell key={i} />);
                      return;
                    }

                    if (
                      cell &&
                      typeof cell === 'object' &&
                      !React.isValidElement(cell)
                    ) {
                      const span = cell.colSpan || 1;
                      skipUntil = i + span;
                      cells.push(
                        <TableCell
                          key={i}
                          colSpan={span}
                          align={cell.align || 'left'}
                          className={cell.className}
                          sx={{
                            fontWeight: 700,
                            fontSize: 13,
                            padding: cell.className ? '10px 12px' : cellPadding,
                            color: '#1B2124',
                            borderBottom: 'none'
                          }}>
                          {cell.value}
                        </TableCell>
                      );
                      return;
                    }

                    cells.push(
                      <TableCell
                        key={i}
                        align={col.align || 'left'}
                        sx={{
                          fontWeight: 700,
                          fontSize: 13,
                          padding: cellPadding,
                          color: '#1B2124',
                          borderBottom: 'none'
                        }}>
                        {cell}
                      </TableCell>
                    );
                  });

                  return cells;
                })()}
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>

      {pagination && (
        <Box
          sx={{
            border: '1px solid #E5E7EB',
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
            background: '#fff',
            px: 2,
            py: 1.5
          }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent='space-between'
            alignItems={{ xs: 'stretch', sm: 'center' }}
            flexWrap='wrap'
            gap={1.5}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: '#6B7280',
                fontFamily: 'Plus Jakarta Sans, sans-serif'
              }}>
              Menampilkan{' '}
              <b style={{ color: '#1B2124' }}>
                {rows.length === 0 ? 0 : (page - 1) * pageSize + 1}
              </b>
              –
              <b style={{ color: '#1B2124' }}>
                {(page - 1) * pageSize + rows.length}
              </b>{' '}
              dari <b style={{ color: '#1B2124' }}>{computedTotal}</b> data
            </Typography>

            <Stack
              direction='row'
              alignItems='center'
              justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
              spacing={1.5}
              sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}>
              {!hidePageSize && (
                <Select
                  value={pageSize}
                  size='small'
                  onChange={e => onPageSizeChange?.(+e.target.value)}
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB'
                    }
                  }}>
                  {[10, 25, 50, 100].map(n => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              )}
              <Pagination
                page={page}
                count={pageCount || 1}
                onChange={(_, value) => onPageChange?.(value)}
                size='small'
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontWeight: 600,
                    fontSize: 13,
                    color: '#1B2124'
                  },
                  '& .Mui-selected': {
                    backgroundColor: '#F7CE00 !important',
                    color: '#1B2124',
                    '&:hover': {
                      backgroundColor: '#E5BE00 !important'
                    }
                  }
                }}
              />
            </Stack>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default TableSiberat;
